import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';

import { environment } from '../../../enviroment';
import { ApiResponse } from '../models/api.models';
import {
  AuthTokens,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginHistoryDto,
  LoginRequest,
  RefreshTokenRequest,
  RegisterRequest,
  ResetPasswordRequest,
  SessionDto,
  UserDto,
  VerifyEmailRequest
} from '../models/auth.models';

const ACCESS_TOKEN_KEY = 'hr_access_token';
const REFRESH_TOKEN_KEY = 'hr_refresh_token';
const USER_KEY = 'hr_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly accessTokenSignal = signal<string | null>(this.readStorage(ACCESS_TOKEN_KEY));
  private readonly refreshTokenSignal = signal<string | null>(this.readStorage(REFRESH_TOKEN_KEY));
  private readonly userSignal = signal<UserDto | null>(this.readUser());
  private refreshInFlight$: Observable<AuthTokens> | null = null;

  readonly accessToken = this.accessTokenSignal.asReadonly();
  readonly refreshToken = this.refreshTokenSignal.asReadonly();
  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.accessTokenSignal());
  readonly displayName = computed(() => {
    const u = this.userSignal();
    if (!u) return '';
    return u.displayName || `${u.firstName} ${u.lastName}`.trim() || u.email;
  });
  readonly rolesLabel = computed(() => (this.userSignal()?.roles ?? []).join(', '));

  login(request: LoginRequest): Observable<AuthTokens> {
    return this.http
      .post<ApiResponse<AuthTokens>>(`${environment.apiUrl}/auth/login`, request)
      .pipe(
        map((res) => this.unwrap(res)),
        tap((tokens) => this.persistSession(tokens))
      );
  }

  register(request: RegisterRequest): Observable<AuthTokens | null> {
    return this.http
      .post<ApiResponse<AuthTokens>>(`${environment.apiUrl}/auth/register`, request)
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.message || 'Registration failed.');
          }
          if (res.data?.accessToken) {
            this.persistSession(res.data);
            return res.data;
          }
          return null;
        })
      );
  }

  refresh(): Observable<AuthTokens> {
    const refreshToken = this.refreshTokenSignal();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available.'));
    }

    // Single-flight: many parallel 401s must share one /auth/refresh call.
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const body: RefreshTokenRequest = { refreshToken };
    this.refreshInFlight$ = this.http
      .post<ApiResponse<AuthTokens>>(`${environment.apiUrl}/auth/refresh`, body)
      .pipe(
        map((res) => this.unwrap(res)),
        tap((tokens) => this.persistSession(tokens)),
        catchError((err) => {
          this.clearSession();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    return this.refreshInFlight$;
  }

  logout(navigate = true): void {
    const refreshToken = this.refreshTokenSignal();
    this.http
      .post<ApiResponse<unknown>>(`${environment.apiUrl}/auth/logout`, { refreshToken })
      .pipe(catchError(() => of(null)))
      .subscribe();

    this.clearSession();
    if (navigate) {
      void this.router.navigate(['/login']);
    }
  }

  forgotPassword(request: ForgotPasswordRequest): Observable<string> {
    return this.http
      .post<ApiResponse<unknown>>(`${environment.apiUrl}/auth/forgot-password`, request)
      .pipe(map((res) => this.unwrapMessage(res)));
  }

  resetPassword(request: ResetPasswordRequest): Observable<string> {
    return this.http
      .post<ApiResponse<unknown>>(`${environment.apiUrl}/auth/reset-password`, request)
      .pipe(map((res) => this.unwrapMessage(res)));
  }

  changePassword(request: ChangePasswordRequest): Observable<string> {
    return this.http
      .post<ApiResponse<unknown>>(`${environment.apiUrl}/auth/change-password`, request)
      .pipe(map((res) => this.unwrapMessage(res)));
  }

  verifyEmail(request: VerifyEmailRequest): Observable<string> {
    return this.http
      .post<ApiResponse<unknown>>(`${environment.apiUrl}/auth/verify-email`, request)
      .pipe(map((res) => this.unwrapMessage(res)));
  }

  getSessions(): Observable<SessionDto[]> {
    return this.http
      .get<ApiResponse<SessionDto[]>>(`${environment.apiUrl}/auth/sessions`)
      .pipe(map((res) => this.unwrapList(res)));
  }

  revokeSession(sessionId: string): Observable<string> {
    return this.http
      .delete<ApiResponse<unknown>>(`${environment.apiUrl}/auth/sessions/${sessionId}`)
      .pipe(map((res) => this.unwrapMessage(res)));
  }

  revokeAllSessions(): Observable<string> {
    return this.http
      .post<ApiResponse<unknown>>(`${environment.apiUrl}/auth/sessions/revoke-all`, {})
      .pipe(map((res) => this.unwrapMessage(res)));
  }

  getLoginHistory(take = 50): Observable<LoginHistoryDto[]> {
    return this.http
      .get<ApiResponse<LoginHistoryDto[]>>(`${environment.apiUrl}/auth/login-history`, {
        params: { take }
      })
      .pipe(map((res) => this.unwrapList(res)));
  }

  hasAnyRole(roles: string[]): boolean {
    if (!roles.length) return true;
    const userRoles = this.userSignal()?.roles ?? [];
    return roles.some((role) =>
      userRoles.some((r) => r.toLowerCase() === role.toLowerCase())
    );
  }

  getAccessToken(): string | null {
    return this.accessTokenSignal();
  }

  getTenantId(): string | null {
    return this.userSignal()?.tenantId ?? null;
  }

  private unwrap(res: ApiResponse<AuthTokens>): AuthTokens {
    if (!res?.success || !res.data) {
      throw new Error(res?.message || 'Authentication failed.');
    }
    return res.data;
  }

  private unwrapMessage(res: ApiResponse<unknown>): string {
    if (!res?.success) {
      throw new Error(res?.message || 'Request failed.');
    }
    return res.message || 'Success';
  }

  private unwrapList<T>(res: ApiResponse<T[]>): T[] {
    if (!res?.success || !res.data) {
      throw new Error(res?.message || 'Request failed.');
    }
    return res.data;
  }

  private persistSession(tokens: AuthTokens): void {
    this.accessTokenSignal.set(tokens.accessToken);
    this.refreshTokenSignal.set(tokens.refreshToken);
    this.userSignal.set(tokens.user);
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(tokens.user));
  }

  private clearSession(): void {
    this.accessTokenSignal.set(null);
    this.refreshTokenSignal.set(null);
    this.userSignal.set(null);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private readUser(): UserDto | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as UserDto) : null;
    } catch {
      return null;
    }
  }
}
